import { useDragClickDetect } from "@/core/composable/useDragClickDetect";
import { ImageSize, useImager } from "@/core/store/imager";
import { arrayGroup } from "@/core/util/arrayGroup";
import { arrayIndex } from "@/core/util/arrayIndex";
import toInt from "@/core/util/toInt";
import { BasketItem } from "@/gql/gen";
import { useBasketItemModel } from "@/modules/shop/composable/useBasketItemModel";
import { ProductModel } from "@/modules/shop/composable/useProduct";
import { useProductFeatures } from "@/modules/shop/composable/useProductFeatures";
import { BasketItemModel } from "@/modules/shop/composable/util";
import { useBasketStore } from "@/modules/shop/store/basket";
import { BasketRuleState, useBasketRulesStore } from "@/modules/shop/store/basket-rules";
import { useCatalogStore } from "@/modules/shop/store/catalog";
import { useCatalogFavStore } from "@/modules/shop/store/catalog-fav";
import { useProductModificatorStore } from "@/modules/shop/store/product-modificator";
import {
    ModificatorState,
    useProductModificatorsComputedStore
} from "@/modules/shop/store/product-modificators-computed";
import { fillBasketItemByProduct } from "@/modules/shop/store/util/basket";
import { ProductDiscountsCalculationOptions } from "@/modules/shop/store/util/benefit";
import { isChildModificator } from "@/modules/shop/store/util/product-modificator";
import { useVorderState } from "@/modules/shop/store/vorder-state";
import { useVorderTotalStore } from "@/modules/shop/store/vorder-total";
// import {useNow} from "@vueuse/core"; // Закомментировано для оптимизации
import { storeToRefs } from "pinia";
import { computed, ref, toRefs, watch } from "vue";
import { useRouter } from "vue-router";

export type ProductCardProps = {
    product: ProductModel;
    basketItemUuid?: string;
    borderStyle?: string | undefined;
    orientation?: string | undefined;
    viewMode?: string | undefined;
    quantityLimit?: number;
    imageHeight?: string;
    mediaBg?: string | boolean;
};

export type ProductCardViewStyle = {
    rowClass: string;
    colMediaClass: string;
    colMainClass: string;
    mediaClass: string;
    title: string;
    weightClass?: string;
};

export type ResponsiveImageConfig = {
    size: ImageSize;
    descriptor: string;
};

export type UseProductParams = {
    giftsVisible?: boolean;
    validateAdd?: any;
    imageSize?: ImageSize;
    visible?: boolean;
    onDetailsRoute?: (product: ProductModel) => any | boolean;
    onDetails?: (product: ProductModel) => void;
    onDetailsBehavior?: "basket" | "product";
    onDetailsProduct?: (product: ProductModel) => void;
    excludeFlags?: string[];
    productOpenAllowSecondary?: boolean;
    calcOptions?: ProductDiscountsCalculationOptions;
    viewModes?: Record<string, ProductCardViewStyle>;
    quantityLimit?: number;
    openDragDetect?: boolean; // Включить защиту от ложных кликов при drag/swipe
    responsiveImages?: ResponsiveImageConfig[]; // Конфигурация адаптивных изображений
};

export function useProductView(
    props: ProductCardProps,
    params: UseProductParams = {}
) {
    const productModificatorStore = useProductModificatorStore();
    const catalogStore = useCatalogStore();
    const basketStore = useBasketStore();
    const basketRulesStore = useBasketRulesStore();
    const totalStore = useVorderTotalStore();
    const vorderState = useVorderState();
    const {imageResolve} = useImager();
    const router = useRouter();

    const {basketOp} = basketStore;
    const {checkModificatorCondition} = productModificatorStore;

    const basketTotal = ref(0)

    const {productModificatorsByProductId} = storeToRefs(
        productModificatorStore
    );
    const {attrs, orderOffice} = storeToRefs(vorderState);
    const {rulesStateMap, specialProducts, productsPriceDiscounted} = storeToRefs(basketRulesStore);

    const {basketItemsModelsByProduct, basketItemsModels} = storeToRefs(basketStore);

    // Безопасный доступ к basketItemsStat с fallback
    const basketItemsStat = computed(() => basketStore.basketItemsStat || {});

    // Глобальные модификаторы
    const modificatorsComputedStore = useProductModificatorsComputedStore();
    const {globalSuggestions, modificatorsStateByOptionProductId} = storeToRefs(modificatorsComputedStore);
    const availableGlobalProducts = computed(() => globalSuggestions.value.products);

    const {product} = toRefs(props);

    const {
        giftsVisible = false,
        validateAdd,
        onDetailsProduct,
        onDetailsBehavior = "product",
        onDetails,
        onDetailsRoute = true,
        excludeFlags,
        quantityLimit,
        openDragDetect = false,
        imageSize = "m-1",
        productOpenAllowSecondary,
        calcOptions = {},
        viewModes = {},
        responsiveImages,
    } = params;

    const productId = product.value.ID || 0;

    const hover = ref<boolean>(false);

    const currentBasketItemUuid = ref(props.basketItemUuid);

    watch(() => props.basketItemUuid, (uuid) => {
        currentBasketItemUuid.value = uuid
    })

    const basketItem = ref({} as BasketItemModel);

    // Drag Detection - защита от ложных кликов при свайпе
    const {isDragging, getListeners, cancelClick} = useDragClickDetect({
        enabled: openDragDetect,
        dragThreshold: 10,
        timeThreshold: 200,
        excludeSelectors: ".prevent-click, button, input, .q-btn",
    });

    const flags = computed(() =>
        product.value.PROPERTIES.FLAGS.map(
            (id) => {
                console.log('flag', {
                    id,
                    val: catalogStore.productFlagsById[id]!,
                    all: catalogStore.productFlagsById
                })
                return catalogStore.productFlagsById[id]!
            }
        ).filter(Boolean)
    );

    // Product Features - Requirements: 3.1, 3.2, 3.4
    const productFeatureIds = computed(() =>
        (product.value.FEATURE_IDS || []).filter((id): id is number => id !== null)
    );
    const {
        features: productFeatures,
        hasFeatures,
        hasInactiveFeatures
    } = useProductFeatures(productFeatureIds);

    const imageUrl = computed(() =>
        imageResolve(product.value.IMAGE?.SRC, imageSize)
    );

    // Конфигурация адаптивных изображений по умолчанию
    const defaultResponsiveImages: ResponsiveImageConfig[] = [
        { size: 'm-1-3', descriptor: '200w' },  // Mobile: 200px (для DPR=1)
        { size: 'm-1-2', descriptor: '300w' },  // Mobile Retina/Tablet: 300px
        { size: 'm-1-2', descriptor: '600w' },    // Desktop: 600px
    ];

    // Генерация srcset для адаптивных изображений
    const imageSrcset = computed(() => {
        const imageSrc = product.value.IMAGE?.SRC;
        if (!imageSrc) return '';

        const configs = responsiveImages || defaultResponsiveImages;

        return configs
            .map(({ size, descriptor }) => {
                const url = imageResolve(imageSrc, size);
                return `${url} ${descriptor}`;
            })
            .join(', ');
    });

    // Атрибут sizes для браузера (соответствует Quasar breakpoints)
    const imageSizes = computed(() => {
        // Quasar breakpoints: xs: 0-599, sm: 600-1023, md: 1024-1439, lg: 1440-1919, xl: 1920+
        // Указываем реальные размеры карточек на разных экранах
        return '(max-width: 599px) 200px, (max-width: 1023px) 300px, 600px';
    });

    const onDetailsRouteDefault = (product: ProductModel) => "/product/" + product.ID

    const _onDetailsRoute = onDetailsRoute
        ? typeof onDetailsRoute === "function"
            ? onDetailsRoute
            : onDetailsRouteDefault
        : false;

    // const now = useNow({interval: 1000 * 5}); // Закомментировано для оптимизации (не используется критично)

    const inBasket = computed(() => basketItem.value.commited);
    const basketQuantity = computed(() => basketItem.value?.fields.QUANTITY || 0);

    const basketInputProps = ref<any>({
        GIFT: null,
    });
    const basketProps = ref<any>({
        GIFT: null,
    });

    const bindContainer = computed(() => {
        const res: any = {
            class: {},
        };
        res.class["--border-style-" + props.borderStyle] = true;
        res.class["--orientation-" + props.orientation] = true;
        res.class["--viewmode-" + props.viewMode] = true;
        if (product.value) {
            res.class["section-" + product.value.IBLOCK_SECTION_ID] = true;
        }
        return res;
    });

    const viewName = computed(() => props.viewMode + "-" + props.orientation);

    const view = computed<ProductCardViewStyle>(
        () =>
            viewModes[viewName.value as any] ||
            ({} as ProductCardViewStyle)
    );

    const bindLinkListeners = computed(() => {
        const res: any = {};
        return res;
    });

    const bindLinkComponent = computed(() => {
        let res = "div";
        if (_onDetailsRoute) {
            res = "a";
        }
        return res;
    });

    const bindLink = computed(() => {
        const res: any = {};
        if (_onDetailsRoute) {
            res.href = _onDetailsRoute(product.value);
        }
        return res;
    });

    const section = computed(() => {
        return product.value.IBLOCK_SECTION_ID
            ? catalogStore.sectionsById[product.value.IBLOCK_SECTION_ID]
            : null;
    });

    const modificators = computed(() => {
        const allModificators = productModificatorsByProductId.value[product.value.ID] || [];

        // Рассчитываем сумму заказа до скидок (для обратной совместимости)
        const orderPriceBeforeDiscounts = basketItemsModels.value.reduce((sum, item) => {
            return sum + (item.fields.PRICE_BASE * item.fields.QUANTITY)
        }, 0);

        // Получаем сумму заказа ПОСЛЕ скидок из vorder-total store
        // Это правильная сумма для проверки ORDER_PRICE_FROM в CONDITION
        const orderPriceAfterDiscounts = totalStore.basketPriceTotal;

        // Фильтруем модификаторы по условиям (CONDITION)
        return allModificators.filter(modificator => {
            const checkResult = checkModificatorCondition(modificator, {
                attrs: attrs.value,
                office: orderOffice.value,
                basketStat: basketItemsStat.value,
                basketItems: basketItemsModels.value,
                orderPriceBeforeDiscounts,
                orderPriceAfterDiscounts
            });
            return checkResult.isActive;
        });
    });

    const modificatorsChild = computed(
        () => modificators.value.filter(item => isChildModificator(item))
    );

    const modificatorsByGroupCode = computed(() =>
        arrayIndex(modificators.value, "GROUP_CODE")
    );
    const modificatorsByPosition = computed(() =>
        arrayGroup(modificators.value, "POSITIONS")
    );

    const descTeaser = computed(() => product.value.PREVIEW_TEXT);
    const basketMutating = computed(() => false);

    const priceAvailable = product.value.isPriceAvailable();
    const weight = product.value.getWeight();
    const setItems = product.value.getSetItems();
    const availableSchedule = null

    const isPriceWithModificators = true;

    // ACTIONS

    const onMouseEnter = () => (hover.value = true);
    const onMouseLeave = () => (hover.value = false);
    const onGiftSelect = (gift: any) => basketItem.value.setGift(877653);

    const setError = (error: any) => {
    };

    const onValidateAdd = () => {
        return true;
        /*
                if (product.REQUIRED_MIN_PRICE && (priceBasketWithoutSpecial.value < product.REQUIRED_MIN_PRICE)) {
                    alert('Сумма заказа меньше ' + product.REQUIRED_MIN_PRICE)
                    return false
                }
                if (product.GIFTS?.length && !basketInputProps.value.GIFT) {
                    alert('Необоходимо выбрать подарок')
                    return false
                }
                if (validateAdd) {
                    const res = validateAdd()
                    if (res !== true) {
                        setError(res)
                        return false
                    }
                }
                setError(false)
                return true

                 */
    };

    const onBasketAdd = async () => {
        // Проверяем, был ли это drag (wasDrag устанавливается синхронно в onMouseUp/onTouchEnd)
        if (isDragging.value) {
            return;
        }

        // Отменяем клик на карточке (чтобы не открылся детальный просмотр)
        cancelClick();

        if (!onValidateAdd || onValidateAdd()) {

            await basketOp({
                action: "add",
                item: basketItem.value as unknown as BasketItemModel, // Передаем item с выбранными модификаторами
                quantity: 1,
                // props: basketProps
            });
        }
    };

    const onBasketQuantity = async (value: number | string | null) => {
        if (isDragging.value) return;
        // Отменяем клик на карточке (чтобы не открылся детальный просмотр)
        cancelClick();
        value = toInt(value);
        if (value >= 0) {
            await basketOp({
                action: "quantity",
                item: basketItem.value as any,
                quantity: value,
            });
        }
    };

    const onClick = (e: Event & { target: HTMLInputElement }) => {
        e.preventDefault();
        e.stopPropagation();
        if (!e.target.closest(".prevent-click")) {
            onDetailsCallback();
        }
    };

    // Объект событий для drag detection (для использования с v-on)
    const dragListeners = getListeners(onClick as any, {
        prevent: true,
        stop: true,
    });

    const onDetailsDefault = () => {
        if (_onDetailsRoute) {
            router.push("/product/" + product.value.ID);
        }
        console.log("OPEN PRODUCT", product);
    };

    const onDetailsCallback = () => {
        if (onDetails) {
            onDetails(product.value);
        } else if (onDetailsBehavior === "basket") {
            onBasketAdd();
        } else if (onDetailsBehavior === "product") {
            if (onDetailsProduct) {
                onDetailsProduct(product.value);
            } else {
                onDetailsDefault();
            }
        }
    };

    // WATCHERS

    const createBasketItem = () => {
        const item = fillBasketItemByProduct(
            {
                QUANTITY: 1,
            } as BasketItem,
            product.value
        );
        return useBasketItemModel(
            item as BasketItem,
            product.value
        );
    }

    const storeBasketItem = computed(() => {
        const items = basketItemsModelsByProduct.value[productId]
        if (items) {
            if (currentBasketItemUuid.value) {
                return items.find(item => item.getUuid() === currentBasketItemUuid.value)
            } else {
                return items[0]
            }
        }
    })

    watch(storeBasketItem, () => {
        if (storeBasketItem.value) {
            basketItem.value = storeBasketItem.value
        } else {
            if (!currentBasketItemUuid.value || !basketItem.value || !basketItem.value?.fields.QUANTITY) {
                basketItem.value = createBasketItem()
            }
        }
    }, {immediate: true});

    const onCreateNewBasketItem = () => {
        basketItem.value = createBasketItem()
        currentBasketItemUuid.value = basketItem.value.getUuid()
    }

    /*

        const calc = ref({} as ProductDiscountsCalculation)
        watch(() => benefitStore.basketBenefitsHash, () => {
            calc.value = calculateProductDiscounts(product.value, calcOptions)
        }, {immediate: true})

         */


    const calc = computed(() => {
        const cached = productsPriceDiscounted.value[product.value.ID]
        if (cached) {
            return cached
        }

        // Fallback: вычисляем напрямую
        return {
            price: product.value.getPrice(),
            priceSource: product.value.getPrice(),
            discountPercent: 0,
        }
    });

    const selectedModificators = computed(() => {
        return basketItem.value.selectedModificators;
    });

    /**
     * Информация о товаре как опции глобальных модификаторов
     */
    const productAsGlobalModificatorOption = computed(() => {
        return availableGlobalProducts.value.filter(
            (item) => item.product.ID === product.value.ID
        );
    });

    /**
     * Является ли товар опцией модификатора (обычного или глобального)
     */
    const isModificatorOption = computed(
        () =>
            productModificatorStates.value.length > 0 ||
            productAsGlobalModificatorOption.value.length > 0
    );

    /**
     * Доступен ли товар для добавления
     * Проверяет basket rules special requirements
     *
     * Requirements: 2.5, 3.4
     */
    const isAvailableToAdd = computed<boolean>(() => {

        if (!product.value.isSaleAllow()) {
            return false
        }
        product.value.IS_SALE_SPECIAL
        // Проверяем basket rules special requirements
        if (hasBasketRuleRequirements.value) {
            // Если есть хотя бы одно активное special правило - товар доступен
            const hasActiveSpecialRule = productSpecialRules.value.some(state => state.active);
            if (!hasActiveSpecialRule) {
                return false;
            }
        }

        // Товар доступен
        return true;
    });

    const totalPrice = computed(() => {
        let resultPrice = calc.value.price;
        if (isPriceWithModificators) {
            resultPrice += Object.values(selectedModificators.value).reduce(
                (acc, modItem) => {
                    if (modItem.mod.TYPE === 'gift')
                        return acc
                    acc += Object.values(modItem.valueOptions).reduce(
                        (acc, optionItem) => {
                            const productId = optionItem.option?.PRODUCT_ID;
                            if (productId) {
                                const product = catalogStore.productsModelsById[productId];
                                const price = productsPriceDiscounted.value[productId]?.price || product?.getPrice() || 0;
                                acc += price;
                            }
                            return acc;
                        },
                        0
                    );
                    return acc;
                }, 0);
        }
        return resultPrice;
    });

    watch(selectedModificators, () => {
        // console.log(productId, toRaw(toValue(selectedModificators)))
    });


    /**
     * Состояния модификаторов для текущего товара
     * Используется для определения доступности и прогресса активации
     */
    const productModificatorStates = computed<ModificatorState[]>(() => {
        return modificatorsStateByOptionProductId.value[product.value.ID] || [];
    });

    /**
     * ============================================================================
     * BASKET RULES REQUIREMENTS
     * ============================================================================
     */

    /**
     * Правила корзины, которые делают товар доступным по special benefit
     */
    const productSpecialRules = computed<BasketRuleState[]>(() => {
        const rules: BasketRuleState[] = [];

        for (const state of Object.values(rulesStateMap.value)) {
            if (!state || !state.rule.PROPERTIES?.BENEFITS_PRODUCT_ID_ENTITIES) continue;

            // Проверяем есть ли товар в BENEFITS_PRODUCT_ID_ENTITIES
            const hasProduct = state.rule.PROPERTIES.BENEFITS_PRODUCT_ID_ENTITIES.some(
                (entity: any) => entity.ID === product.value.ID
            );

            if (hasProduct) {
                rules.push(state);
            }
        }

        return rules;
    });

    /**
     * Есть ли у товара требования по basket rules (special)
     */
    const hasBasketRuleRequirements = computed(() => {
        return productSpecialRules.value.length > 0;
    });

    /**
     * Неактивные правила special для товара
     */
    const inactiveSpecialRules = computed<BasketRuleState[]>(() => {
        return productSpecialRules.value.filter(state => !state.active);
    });

    /**
     * Данные для BasketRuleRequirement компонента (detailed режим)
     */
    const detailedBasketRuleRequirements = computed<BasketRuleState[]>(() => {
        // Показываем только если товар недоступен из-за неактивных special правил
        if (!hasBasketRuleRequirements.value || inactiveSpecialRules.value.length === 0) {
            return [];
        }

        return inactiveSpecialRules.value;
    });

    return {
        totalPrice,
        modificators,
        modificatorsChild,
        modificatorsByGroupCode,
        modificatorsByPosition,
        availableSchedule,
        onClick,
        product,
        flags,
        calc,
        weight,
        priceAvailable,
        basketItem,
        basketMutating,
        basketProps,
        basketInputProps,
        basketQuantity,
        onGiftSelect,
        onBasketAdd,
        onBasketQuantity,
        onValidateAdd,
        openProduct: onDetails,
        setError,
        imageUrl,
        imageSrcset,
        imageSizes,
        bindContainer,
        bindLink,
        bindLinkListeners,
        view,
        descTeaser,
        bindLinkComponent,
        setItems,
        inBasket,
        onMouseEnter,
        onMouseLeave,
        hover,
        // now, // Закомментировано для оптимизации
        selectedModificators,
        quantityLimit,
        productAsGlobalModificatorOption,
        isModificatorOption,
        isAvailableToAdd,
        // Drag Detection (только если openDragDetect=true)
        isDragging,
        dragListeners, // Объект событий для v-on
        cancelClick, // Метод для отмены клика на карточке (используется в кнопках)
        onCreateNewBasketItem,
        currentBasketItemUuid,
        // Product Features - Requirements: 3.1, 3.2, 3.4
        productFeatures,
        hasFeatures,
        hasInactiveFeatures,
        // Новое API - состояния модификаторов
        productModificatorStates,
        // Basket Rules Requirements
        hasBasketRuleRequirements,
        productSpecialRules,
        inactiveSpecialRules,
        detailedBasketRuleRequirements,
        viewName
    };
}

export type UseProductActionsProps = {
    product: ProductModel;
    hover?: boolean;
};

export function useProductActions(props: UseProductActionsProps) {
    const favStore = useCatalogFavStore();
    const {favoriteProductIds} = storeToRefs(favStore);

    const {product, hover} = toRefs(props);

    const inFav = computed(
        () => !!favoriteProductIds.value[product.value.ID.toString()]
    );

    const favMutating = computed(() => false);

    const favToggle = () => {
        favStore.favToggle(product.value.ID);
    };

    return {
        product,
        inFav,
        favMutating,
        favToggle,
    };
}
